<template>
  <div>
    <!-- Inline -->
    <div class="row q-mb-md">
      <!-- Filters hide/show button -->
      <div class="col-6">
        <div class="row" style="justify-content: flex-start">
          <div>
            <q-btn
              @click="showFilters();"
              :text-color="hideFilters ? 'grey-5' : 'white'"
              class="neutral-btn ctrl-button btn-compact"
              :class="{ primary: !hideFilters }"
              icon="filter_list"
              :icon-right="hideFilters ? 'keyboard_arrow_up' : 'keyboard_arrow_down'"
            />
          </div>
          <!-- Searchfield -->
          <div class="q-ml-md" style="flex-grow: 1">
            <q-input
              :disable="!hideFilters || activeFilters"
              color="grey-5"
              v-model="searchTerm"
              @input="applyFilters"
              :placeholder="getString('module.logs.list.filter.label')"
            />
          </div>
        </div>
      </div>
    </div>
    <!-- Filters block -->
    <transition name="fade">
    <div v-if="!hideFilters" class="table-block q-mb-md">
      <div class="row q-pt-md q-pl-md q-pr-md" style="justify-content: space-between;">
        <!-- Select field -->
        <div class="col-6 q-pb-md q-pl-md q-pr-md">
          <q-select
            multiple
            @input="applyFilters"
            v-model="filters.level"
            :options="levelOptions"
            :float-label="getString('module.logs.list.table.column.level')"/>
        </div>
        <!-- Select field -->
        <div class="col-6 q-pb-md q-pl-md q-pr-md">
          <q-select
            multiple
            v-model="filters.source"
            @input="applyFilters"
            :options="sourceOptions"
          :float-label="getString('module.logs.list.table.column.source')"/>
        </div>
      </div>
      <div class="row q-pb-md q-pl-md q-pr-md">
        <!-- Select field -->
        <div class="col-6 q-pb-md q-pl-md q-pr-md">
          <q-select
            multiple
            v-model="filters.method"
            @input="applyFilters"
            :options="methodOptions"
          :float-label="getString('module.logs.list.table.column.method')"/>
        </div>
        <!-- Inputfield -->
        <div class="col-6 q-pl-md q-pr-md">
          <q-input color="primary"
          v-model="filters.message"
          @input="applyFilters"
          :float-label="getString('module.logs.list.table.column.message')"/>
        </div>
      </div>
      <div class="row q-pl-md q-pr-md" style="justify-content: space-between;">
        <div></div>
        <div class="q-pl-md q-pr-md q-pb-md" style="vertical-align: middle;">
          <q-btn
            :disable="!activeFilters"
            @click="resetFilters();"
            class="capitalize ctrl-button"
            :color="activeFilters ? 'primary' : 'grey-4'"
            label="Reset"
          />
        </div>
      </div>
    </div>
    </transition>
    <!-- Data table -->
    <div class="q-table" id="myTable">
      <vuetable
        ref="vuetable"
        class="table-block q-mb-md"
        :class="{ 'is-loading': isLoading }"
        :table-height="tableHeight"
        :api-url="endpoint"
        pagination-path
        :api-mode="true"
        :noDataTemplate="noDataString(responseData.multiple)"
        :css="css.table"
        :http-fetch="fetchData"
        :fields="columns"
        :row-class="selectable ? applyRowClass : null"
        @vuetable:loading="onLoading"
        @vuetable:load-success="onSuccess"
        @vuetable:load-error="onError"
        @vuetable:pagination-data="onPaginationData"
        @vuetable:row-clicked="onRowClicked">
        <!-- Custom fields -->
        <!-- Messahe slot -->
        <div slot="level" slot-scope="props" class="message-slot">
          <div class="log-message" :class="messageClass(props.rowData.level)"></div>
          <div>
            {{ props.rowData.level | capitalize }}
          </div>
        </div>
        <div slot="source" slot-scope="props">
          {{ props.rowData.source | capitalize }}
        </div>
        <div slot="message" slot-scope="props">
          <div v-html="highlight(props.rowData.message, filters.message)"></div>
        </div>
      </vuetable>
      <!-- Pagination -->
      <div class="row pagination-bottom" id="myPagination">
        <!-- Per page select -->
        <div class="col">
          <div class="row">
            <span class="q-mr-sm grey">
              {{ getString('list.itemsPerPage') }}
            </span>
            <q-select
              class="btn-compact select-small"
              hide-underline
              color="grey"
              v-model="perPage"
              :options="perPageOptions"
            />
          </div>
        </div>
        <div class="col right-margin">
          <vuetable-pagination
            :on-each-side="1"
            :css="css.pagination"
            @vuetable-pagination:change-page="onChangePage"
            ref="pagination">
          </vuetable-pagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vuetable from 'vuetable-2';
import VuetablePagination from 'vuetable-2/src/components/VuetablePagination.vue';

import getString from '@/helpers/GetStrings';

// Mixins
import tableMixin from '@/mixins/tables/tableMixin';
import pagination from '@/mixins/tables/pagination';
import searchHighlight from '@/mixins/tables/searchHighlight';
import fetchData from '@/mixins/tables/fetchData';
import common from '../common';

export default {
  mixins: [
    tableMixin,
    pagination,
    searchHighlight,
    fetchData,
    common,
  ],
  components: {
    Vuetable,
    VuetablePagination,
  },
  data() {
    return {
      // dataObject refers to the key of the data object from the response. eg. response.data.logs
      isLoading: false,
      selectable: false,
      columns: [
        {
          name: '__slot:level',
          title: getString('module.logs.list.table.column.level'),
          sortField: 'level',
          titleClass: 'text-left sortable vcol-s',
          dataClass: 'text-left vcol-s text-ellipsis',
        },
        {
          name: '__slot:source',
          title: getString('module.logs.list.table.column.source'),
          sortField: 'source',
          titleClass: 'text-left sortable vcol-s',
          dataClass: 'text-left vcol-s text-ellipsis',
        },
        {
          name: 'module',
          title: getString('module.logs.list.table.column.module'),
          sortField: 'module',
          titleClass: 'text-left sortable vcol-s',
          dataClass: 'text-left vcol-s text-ellipsis',
        },
        {
          name: '__slot:message',
          title: getString('module.logs.list.table.column.message'),
          sortField: 'message',
          titleClass: 'text-left sortable vcol-xl',
          dataClass: 'text-left vcol-xl text-ellipsis',
        },
        {
          name: 'method',
          title: getString('module.logs.list.table.column.method'),
          sortField: 'method',
          titleClass: 'text-left sortable vcol-s',
          dataClass: 'text-left vcol-s text-ellipsis',
        },
        {
          name: 'datetime',
          title: 'Timestamp',
          label: 'datetime',
          sortField: 'datetime',
          titleClass: 'text-left sortable vcol-m',
          dataClass: 'text-left vcol-m text-ellipsis',
          callback: 'tableFormatTime',
        },
      ],
      searchTerm: '',
      filters: {
        level: [],
        source: [],
        method: [],
        message: '',
      },
      levelOptions: [
        {
          label: 'Message',
          value: 'message',
        },
        {
          label: 'Warning',
          value: 'warning',
        },
        {
          label: 'Error',
          value: 'error',
        }],
      sourceOptions: [
        {
          label: 'Back-end',
          value: 'back-end',
        },
        {
          label: 'Front-end',
          value: 'front-end',
        },
      ],
      methodOptions: [
        {
          label: 'GET',
          value: 'GET',
        },
        {
          label: 'POST',
          value: 'POST',
        },
        {
          label: 'PUT',
          value: 'PUT',
        },
        {
          label: 'DELETE',
          value: 'DELETE',
        },
        {
          label: 'CLI',
          value: 'CLI',
        },
      ],
      logLevel: {
        message: 'message-level',
        error: 'error-level',
        warning: 'warning-level',
      },
      hideFilters: true,
      tableHeight: 'calc(100vh - 280px)',
      css: {
        table: {
          tableClass: 'vuetable ui selectable celled stackable attached table vuetable-fixed-layout',
          loadingClass: '',
          ascendingIcon: 'mdi mdi-chevron-up icon',
          descendingIcon: 'mdi mdi-chevron-down icon',
          handleIcon: '',
        },
        pagination: {
          wrapperClass: 'pagination',
          activeClass: 'active',
          disabledClass: 'disabled',
          pageClass: '',
          linkClass: '',
          icons: {
            first: 'mdi mdi-chevron-double-left icon',
            prev: 'mdi mdi-chevron-left icon',
            next: 'mdi mdi-chevron-right icon',
            last: 'mdi mdi-chevron-double-right icon',
          },
        },
      },
    };
  },
  methods: {
    getString,
    messageClass(rowData) {
      return this.logLevel[rowData];
    },
  },
};
</script>

<style lang="stylus" scoped>
@import '~variables';
@import './../../../styles/components/tables.general.styl';

.log-message
  width: 6px
  height 6px
  border-radius: 50%
  margin 0.225rem 0.5rem 0 0

.message-level
  background-color $positive

.error-level
  background-color $ctrl-red

.warning-level
  background-color: $ctrl-yellow

.message-slot
  vertical-align middle
  display inline-flex
</style>
